import { NextPage } from 'next'
import { makeLocaleStaticProps } from 'web_core/utils/static_props'
import { DefaultLayout } from '~/components/Layout/Default'

export const getStaticProps = makeLocaleStaticProps

const Page: NextPage = () => {
  return (
    <DefaultLayout
      head={{
        title: 'Mathpediaの理念',
        description:
          'Mathpediaは、大学レベル以上の数学を中心に、現代数学を整理することを目的としたプロジェクトです。現代数学を整理し見通しを良くすること、数学を書く仕事に対価を払うこと、助け合える数学コミュニティを作ることの3つをミッションとして運営しております。',
      }}
      categoriesMenu={{ pc: true, sp: true, spFixed: true }}
      leftSidebar
      rightSideBar
    >
      <div className="mathdown-monotone">
        <h1>Mathpediaの理念</h1>

        <p>
          Mathpediaは、大学レベル以上の数学を中心に、現代数学を整理することを目的としたプロジェクトです。運営資金は、プロジェクト発足人および支援者の方々からの寄付と、参考文献のアフィリエイト紹介料により担われております。我々がこのプロジェクトで目指すミッションは、以下の3つです。
        </p>

        <h2>現代数学を整理し、見通しを良くする</h2>

        <p>
          高校までの数学が具体的な計算を軸に進むのに対し、大学以降の数学は抽象度が高く、多くの理論へ枝分かれしていきます。そのため学習の道筋も全体像もつかみにくい。大学の課程においてさえ、学部で扱われるのは各分野の基礎事項が点在する形にとどまり、大学院で出会う専門的な理論との間には大きな隔たりがあります。
        </p>

        <p>
          この隔たりは、教科書を増やすだけでは埋まりません。教科書は一冊ごとに前提と記法を選び直すため、隣の分野の本を開くと、同じ概念が別の名前と別の流儀で現れます。読者は「これは自分が知っているあの概念なのか、違うものなのか」の判断に手間を取られ、そこで足が止まる。
          <strong>
            理論と理論のあいだにあるこの摩擦を減らすことが、事典にできる仕事
          </strong>
          だと我々は考えます。
        </p>

        <p>
          そのために我々は、一つの語に一つの記事を対応させ、定義・性質・例・反例をそれぞれ独立した単位として書き、記事どうしを依存関係で結ぶ方針をとっています。「この記事を読む前に何を知っている必要があるか」「この定理はどの仮定を落とすと成り立たなくなるか」が読者に見える形になっていることが、点在した知識を道筋に変えると考えるからです。とくに反例については、それが何であり、何の例として使われ、
          <strong>どの性質とどの性質を分離するのか</strong>
          までを構造化して記録しています。
        </p>

        <h2>数学を書く仕事に対価を払う</h2>

        <p>
          複雑な理論を整理し、初学者に届く形に書き直す仕事には、研究とは別種の、しかし決して軽くはない労力が要ります。どの順で述べれば理解が積み上がるか、どの例を最初に置くか、どの一般化を今は避けるか——こうした判断は、その理論を十分に自分のものにした人にしかできません。
        </p>

        <p>
          それにもかかわらず、解説を書く労働はほとんど評価されません。論文には業績としての位置がありますが、良い解説を書いても、それが研究者としての評価につながることは稀です。結果として、この仕事は個人の善意と余暇に依存し、数学を志す方々が置かれた経済的な状況とは切り離されたままになっています。
        </p>

        <p>
          我々の第二の目的は、この労働に対価を払うことです。Mathpediaの記事を執筆・監修していただける方々には対価をお渡ししております。現時点で計画があるわけではありませんが、今後より多くの活動資金が集まれば、経済的に困窮した学生や研究者の方々への資金援助なども行っていきたいと考えております。
        </p>

        <h2>助け合える数学コミュニティを作る</h2>

        <p>
          数学は、極めて高い厳密性と幅広い抽象理論への理解を要求する一方で、
          <strong>
            一歩隣の分野に踏み出せば初歩的なことも分からなくなってしまう孤独な学問
          </strong>
          です。元々数学が好きで学問の道に入った人であっても、経済的に疲弊してしまったり、延々と続く抽象的な議論が嫌になって投げ出してしまう経験をされた方は少なくないのではないでしょうか。
        </p>

        <p>
          だからこそ、我々は数学と向き合ううえで最も大切なことは助け合いの精神だと考えます。過去に学んだ者からこれから学ぶ者への学術的なバトンタッチ、経済的に余裕がある者から乏しい者へのバトンタッチを通して、必ずしも学術的環境・経済的環境に恵まれていなくとも、人類未踏の新たな境地に達する人々が現れてくるはずだと我々は信じております。
        </p>

        <p>
          このバトンは一方向のものではありません。記事の誤りを指摘していただくこと、説明の順序に異議を唱えていただくこと、抜けている概念を教えていただくことも、我々にとっては同じバトンです。Mathpediaはあくまで一手段に過ぎず、我々は今後も新たな取り組みを試行錯誤し、助け合える数学コミュニティづくりに勤しんでまいります。
        </p>

        <h2>記事の作り方と、責任の所在</h2>

        <h3>誰が決めているか</h3>

        <p>
          <strong>
            何を記事にするか、どの定義を採るか、どこまで書くか、そして公開してよいかは、すべて人間が決めています。
          </strong>
          執筆にあたっては大規模言語モデル（LLM）を補助として使っており、下書きの作成、記述の機械的な点検、記録の照合といった作業を任せています。記事の内容に対する責任は、道具を使った我々にあります。
        </p>

        <h3>LLMを使うことについて</h3>

        <p>
          LLMが数学的に誤った内容をもっともらしい文体で書くことは、我々自身が日々目にしています。「一見それらしい証明」を大量に生産できてしまうことは、数学にとって利益ではなく危険です。数学に携わる方々がこの技術に警戒を向けるのは、もっともなことだと考えています。
        </p>

        <p>
          そのうえで我々がLLMを使っているのは、
          <strong>警戒に見合うだけの検証の工程を用意できる</strong>
          と考えるからです。以下はその工程です。誇張なく書きます。
        </p>

        <h3>主張と根拠の対応づけ</h3>

        <p>
          記事に書くすべての主張について、それがどの公理・どの定義・どの既知の結果から導かれるのかを対応づけた記録を、公開記事とは別に作ります。「この記事はどこまでを前提として受け入れ、どこからを自分で証明したか」を、あとから誰でも辿れる形にしておくためです。文献に依拠する部分は「引用のみ」として明確に区別し、記事の数学的な骨格がその引用に依存しないように書きます。
        </p>

        <h3>独立検証</h3>

        <p>
          <strong>
            書き上がった記事は、執筆したのとは別の担当が、執筆側の記録を根拠に使わずに検証します。
          </strong>
          検証側は記事のすべてのブロックと要約を自分で再構成し、主張が前提から実際に導けるかを一つずつ確かめます。「執筆側がそう書いているから正しい」という判断は認めていません。
        </p>

        <p>
          指摘は、数学的な誤りと記録の不備とを区別して記録し、すべてに対応したうえで
          <strong>改めて検証にかけます</strong>
          。未解決の指摘が一件でも残っている記事は公開しません。この往復は記事によっては十回近くに及びます。実際に、要約の一文が本文より強い主張になっている（仮定が一つ落ちている）という誤りが、この工程で見つかって訂正された例があります。
        </p>

        <h3>記録の保持</h3>

        <p>
          各記事について、どの版をいつ検証し、何を指摘され、どう直したかの記録を保持しています。記事本文・要約・内部の証明記録それぞれのハッシュ値を控えており、検証を受けた版があとから書き換えられていないことを機械的に確認できるようにしています。
        </p>

        <h3>この工程の限界</h3>

        <p>以下は率直に申し上げます。</p>

        <ul>
          <li>
            <strong>検証を行っているのもLLMです。</strong>
            LLMが書いたものをLLMが検証することが、人間の査読の代わりにならないことは承知しています。我々がこの工程に意味があると考えるのは、実際に誤りが見つかり訂正されているからであって、原理的に正しさが保証されるからではありません。
          </li>
          <li>
            <strong>形式証明支援系による検証ではありません。</strong>
            CoqやLeanで機械的に証明が通っていることを確認したわけではありません。
          </li>
          <li>
            <strong>
              参考文献の巻号・頁・節番号は、原本で確認できていないものがあります。
            </strong>
            該当箇所は内部の記録上そのように区別しています。
          </li>
          <li>
            以上を経てもなお、
            <strong>誤りが残る可能性を排除できません。</strong>
          </li>
        </ul>

        <p>
          <strong>
            誤りを見つけられた場合は、ぜひ{' '}
            <a href="mailto:admin@math.jp">admin@math.jp</a>{' '}
            までお知らせください。
          </strong>
          いただいた指摘は記録に残し、対応の経緯とともに反映します。数学は一人では正しさを保証できない学問であり、それは我々の工程についても同じです。この点において、我々は読者の方々を、監視される側と監視する側としてではなく、同じ作業に加わってくださる方として見ています。
        </p>
      </div>
    </DefaultLayout>
  )
}

export default Page
